import { createEffect, createSignal, For, Show, Suspense, createMemo, onMount, onCleanup } from "solid-js";
import { A, createAsync, revalidate, useAction, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import { Button, Badge, Card, CardContent, Skeleton, Select } from "~/components/ui";
import { InboxCard, InboxFilters, InboxStatsBar } from "~/components/inbox";
import { getInboxVideos, getInboxStats, getChannels, getTopics, triageVideo } from "~/server/bff";
import { INVALIDATION_GROUPS } from "~/server/bff/cache-keys";
import { useHotkeys } from "~/lib/hooks/useHotkeys";
import type { Video } from "~/lib/types";
import {
  Archive, Clock, Check, ChevronDown, Inbox as InboxIcon
} from "lucide-solid";

export const route = {
  preload: () => Promise.all([getInboxVideos(), getInboxStats(), getChannels(), getTopics()]),
} satisfies RouteDefinition;

export default function InboxPage() {
  const [selectedIndex, setSelectedIndex] = createSignal(0);
  const [selectedVideoId, setSelectedVideoId] = createSignal<string | null>(null);
  const [priorityFilter, setPriorityFilter] = createSignal<string>("all");
  const [channelFilter, setChannelFilter] = createSignal<string>("all");
  const [topicFilter, setTopicFilter] = createSignal<string>("all");

  const triageAction = useAction(triageVideo);

  const videos = createAsync(() => getInboxVideos());
  const stats = createAsync(() => getInboxStats());
  const channels = createAsync(() => getChannels());
  const topics = createAsync(() => getTopics());

  const priorityOptions = [
    { value: "all", label: "All Priorities" },
    { value: "flash", label: "🔴 Flash" },
    { value: "high", label: "🟠 High" },
    { value: "normal", label: "🟢 Normal" },
  ];

  const channelOptions = createMemo(() => {
    const chans = channels();
    if (!chans) return [{ value: "all", label: "All Sources" }];
    return [
      { value: "all", label: "All Sources" },
      ...chans.map((c) => ({ value: c.id, label: c.name })),
    ];
  });

  const topicOptions = createMemo(() => {
    const tops = topics();
    if (!tops) return [{ value: "all", label: "All Topics" }];
    return [
      { value: "all", label: "All Topics" },
      ...tops.map((t) => ({ value: t.id, label: t.name })),
    ];
  });

  const filteredVideos = createMemo(() => {
    const vids = videos();
    if (!vids) return null;
    return vids.filter((v) => {
      if (priorityFilter() !== "all" && v.priority !== priorityFilter()) return false;
      if (channelFilter() !== "all" && v.channelId !== channelFilter()) return false;
      return true;
    });
  });

  createEffect(() => {
    const current = filteredVideos();
    const count = current ? current.length : 0;
    if (count === 0) {
      setSelectedIndex(0);
      return;
    }
    setSelectedIndex((index) => Math.min(index, count - 1));
  });

  const handleKeyNavigation = (e: KeyboardEvent) => {
    const vids = filteredVideos();
    if (!vids || !vids.length) return;

    switch (e.key) {
      case "j":
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, vids.length - 1));
        break;
      case "k":
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
      case "o":
        e.preventDefault();
    const video = vids[selectedIndex()];
        if (video) {
          setSelectedVideoId(video.id);
        }
        break;
      case "a":
        e.preventDefault();
        handleTriage(vids[selectedIndex()]?.id, "approved");
        break;
      case "x":
        e.preventDefault();
        handleTriage(vids[selectedIndex()]?.id, "archived");
        break;
      case "s":
        e.preventDefault();
        handleTriage(vids[selectedIndex()]?.id, "snoozed");
        break;
      case "Escape":
        e.preventDefault();
        setSelectedVideoId(null);
        break;
    }
  };

  onMount(() => {
    document.addEventListener("keydown", handleKeyNavigation);
  });
  
  onCleanup(() => {
    if (typeof document !== "undefined") {
      document.removeEventListener("keydown", handleKeyNavigation);
    }
  });

  const handleTriage = async (videoId: string | undefined, status: "approved" | "archived" | "snoozed") => {
    if (!videoId) return;
    const formData = new FormData();
    formData.set("videoId", videoId);
    formData.set("status", status);
    await triageAction(formData);
    await revalidate([...INVALIDATION_GROUPS.VIDEO_TRIAGE]);
  };

  return (
    <AppShell title="Inbox">
      <div class="p-6 max-w-6xl mx-auto">
        <PageHeader
          title="Inbox"
          description="Review and triage new videos from your monitored channels"
          actions={
            <div class="flex items-center gap-2 text-sm text-muted-foreground">
              <kbd class="px-2 py-1 bg-muted rounded text-xs">j/k</kbd>
              <span>navigate</span>
              <kbd class="px-2 py-1 bg-muted rounded text-xs">a</kbd>
              <span>approve</span>
              <kbd class="px-2 py-1 bg-muted rounded text-xs">x</kbd>
              <span>archive</span>
            </div>
          }
        />

        <Suspense fallback={<InboxStatsSkeleton />}>
          <Show when={stats()}>
            {(s) => <InboxStatsBar stats={s()} />}
          </Show>
        </Suspense>

        <InboxFilters
          priorities={priorityOptions}
          channels={channelOptions()}
          topics={topicOptions()}
          selectedPriority={priorityFilter() === "all" ? undefined : priorityFilter()}
          selectedChannel={channelFilter() === "all" ? undefined : channelFilter()}
          selectedTopic={topicFilter() === "all" ? undefined : topicFilter()}
          onPriorityChange={(value) => setPriorityFilter(value ?? "all")}
          onChannelChange={(value) => setChannelFilter(value ?? "all")}
          onTopicChange={(value) => setTopicFilter(value ?? "all")}
          onClearFilters={() => {
            setPriorityFilter("all");
            setChannelFilter("all");
            setTopicFilter("all");
          }}
        />

        <div class="space-y-3">
          <Suspense fallback={<InboxListSkeleton />}>
            <Show when={filteredVideos()} fallback={<InboxListSkeleton />}>
              {(items) => (
                <Show
                  when={items().length > 0}
                  fallback={
                    <Card>
                      <CardContent class="flex flex-col items-center justify-center py-12">
                        <InboxIcon class="h-12 w-12 text-muted-foreground mb-4" />
                        <h3 class="text-lg font-medium mb-2">All caught up!</h3>
                        <p class="text-muted-foreground text-center">
                          No new videos to review. Check back later or adjust your filters.
                        </p>
                      </CardContent>
                    </Card>
                  }
                >
                  <For each={items()}>
                    {(video, index) => (
                      <InboxCard
                        video={video}
                        isSelected={index() === selectedIndex()}
                        onSelect={() => setSelectedIndex(index())}
                        onApprove={() => handleTriage(video.id, "approved")}
                        onArchive={() => handleTriage(video.id, "archived")}
                        onSnooze={() => handleTriage(video.id, "snoozed")}
                      />
                    )}
                  </For>
                </Show>
              )}
            </Show>
          </Suspense>
        </div>
      </div>
    </AppShell>
  );
}

function InboxStatsSkeleton() {
  return (
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <For each={[1, 2, 3, 4]}>
        {() => <Skeleton class="h-24" />}
      </For>
    </div>
  );
}

function InboxListSkeleton() {
  return (
    <div class="space-y-3">
      <For each={[1, 2, 3, 4, 5]}>
        {() => <Skeleton class="h-32" />}
      </For>
    </div>
  );
}
